import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, InfoWindow, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { ArrowLeftIcon, PlayIcon, PauseIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminScreen, ScreenPathPoint } from '@app/Screens/types';
import { getScreens, getScreenPath, setScreenEnabled } from '@app/Screens/screens.api';
import { StatusBadge } from '@app/Screens/StatusBadge';
import { segmentTrail } from '@app/Screens/trail';
import {
  relativeTime,
  formatDateTime,
  formatCoord,
  formatSpeed,
  formatCourse,
  formatFixQuality,
  formatDistance,
  formatGap,
  haversineMeters,
  playingFor
} from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

const WINDOWS: { label: string; hours: number }[] = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 }
];

export const ScreenDetailPage: React.FunctionComponent = () => {
  const { screenId } = useParams();
  const id = Number(screenId);
  const navigate = useNavigate();

  const [screen, setScreen] = useState<AdminScreen | null>(null);
  const [path, setPath] = useState<ScreenPathPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(24);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [gapMinutes, setGapMinutes] = useState(5);

  const apiKey = process.env.GOOGLE_MAPS_API_KEY || '';
  const { isLoaded } = useJsApiLoader({ id: 'advolt-admin-google-maps', googleMapsApiKey: apiKey });

  // Load the screen's metadata from the status list (no per-screen endpoint yet).
  useEffect(() => {
    let cancelled = false;
    getScreens()
      .then(list => {
        if (cancelled) return;
        setScreen(list.find(s => s.screenId === id) || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Load the GPS path for the selected time window.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const since = new Date(Date.now() - windowHours * 3600_000).toISOString();
    getScreenPath(id, since)
      .then(points => {
        if (cancelled) return;
        setPath(points);
        setCursor(points.length ? points.length - 1 : 0);
        setPlaying(false);
        setSelected(null);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load GPS path');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, windowHours]);

  // Animate the scrubber when playing.
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!playing || path.length < 2) return;
    timerRef.current = window.setInterval(() => {
      setCursor(c => {
        if (c >= path.length - 1) {
          setPlaying(false);
          return c;
        }
        return c + 1;
      });
    }, 150);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing, path.length]);

  const latLngs = useMemo(() => path.map(p => ({ lat: p.latitude, lng: p.longitude })), [path]);
  const current = path[Math.min(cursor, path.length - 1)];

  // Break the trail into contiguous "runs" wherever two consecutive fixes are
  // more than `gapMinutes` apart. Each gap (the bridge between two runs) is drawn
  // as a dashed warning line instead of a solid route, since there's no real GPS
  // data spanning it.
  const gapMs = Math.max(1, gapMinutes) * 60_000;
  const { runs, gapStarts } = useMemo(() => segmentTrail(path, gapMs), [path, gapMs]);

  // Pick the breadcrumb nearest to where the trail was clicked. Planar lat/lng
  // distance is fine for a nearest-point selection at city scale.
  const selectNearest = (e: google.maps.PolyMouseEvent | google.maps.MapMouseEvent) => {
    const ll = e.latLng;
    if (!ll || path.length === 0) return;
    const clat = ll.lat();
    const clng = ll.lng();
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < path.length; i++) {
      const dLat = path[i].latitude - clat;
      const dLng = path[i].longitude - clng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setSelected(best);
  };

  const selectedPoint = selected != null ? path[selected] : undefined;
  const prevPoint = selected != null && selected > 0 ? path[selected - 1] : undefined;
  const center = current
    ? { lat: current.latitude, lng: current.longitude }
    : screen?.latitude != null && screen?.longitude != null
    ? { lat: screen.latitude, lng: screen.longitude }
    : { lat: 39.5, lng: -98.35 };

  const onPlayToggle = () => {
    if (!playing && cursor >= path.length - 1) setCursor(0);
    setPlaying(p => !p);
  };

  const enabled = screen ? screen.enabled !== false : true;
  const onToggleEnabled = async () => {
    if (!screen) return;
    const next = !enabled;
    if (!window.confirm(`${next ? 'Enable' : 'Disable'} this screen?`)) return;
    setBusy(true);
    try {
      const res = await setScreenEnabled(id, next);
      setScreen({ ...screen, enabled: res.enabled });
      toast(`Screen ${res.enabled ? 'enabled' : 'disabled'}`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update screen');
    } finally {
      setBusy(false);
    }
  };

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    back: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: 'transparent',
      border: 'none',
      color: colors.accent,
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      padding: 0,
      marginBottom: 16
    },
    headerRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 4 },
    title: { fontSize: 24, fontWeight: 800, color: colors.title, margin: 0 },
    sub: { fontSize: 13, color: colors.subtle, marginBottom: 20 },
    statRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
    stat: {
      flex: '1 1 160px',
      background: '#fff',
      border: `1px solid ${colors.border}`,
      borderRadius: radii.card,
      padding: '14px 16px'
    },
    statLabel: { fontSize: 12, fontWeight: 600, color: colors.subtle, textTransform: 'uppercase', letterSpacing: '0.3px' },
    statValue: { fontSize: 18, fontWeight: 700, color: colors.title, marginTop: 4 },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, padding: 16 },
    sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: colors.title },
    playBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 16px',
      border: 'none',
      borderRadius: radii.button,
      background: colors.accent,
      color: '#fff',
      fontSize: 14,
      fontWeight: 600,
      cursor: path.length < 2 ? 'default' : 'pointer',
      opacity: path.length < 2 ? 0.5 : 1
    },
    info: { minWidth: 230, maxWidth: 280, fontSize: 13, color: colors.title, lineHeight: 1.4 },
    infoHead: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 8,
      paddingBottom: 8,
      borderBottom: `1px solid ${colors.border}`
    },
    infoTitle: { fontSize: 13, fontWeight: 700, color: colors.title },
    infoTime: { fontSize: 12, color: colors.subtle, whiteSpace: 'nowrap' },
    infoRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' },
    infoLabel: { color: colors.subtle, fontWeight: 600, whiteSpace: 'nowrap' },
    infoValue: { textAlign: 'right' },
    infoLink: {
      background: 'transparent',
      border: 'none',
      padding: 0,
      color: colors.accent,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none'
    },
    infoJump: {
      marginTop: 10,
      width: '100%',
      padding: '7px 12px',
      border: 'none',
      borderRadius: radii.button,
      background: colors.accent,
      color: '#fff',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer'
    }
  };

  const windowBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    border: `1px solid ${active ? colors.accent : colors.border}`,
    background: active ? colors.accent : '#fff',
    color: active ? '#fff' : colors.muted,
    borderRadius: radii.button,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  });

  const stat = (label: string, value: React.ReactNode) => (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );

  const infoRow = (label: string, value: React.ReactNode) => (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value}</span>
    </div>
  );

  return (
    <div style={styles.wrap}>
      <button style={styles.back} onClick={() => navigate('/screens')}>
        <ArrowLeftIcon /> Back to Screens
      </button>

      <div style={styles.headerRow}>
        <h1 style={styles.title}>{screen?.displayName || `Screen #${id}`}</h1>
        {screen && <StatusBadge status={screen.status} pulse />}
        {screen?.enabled === false && (
          <span style={{ color: colors.danger, fontWeight: 700, fontSize: 13 }}>disabled</span>
        )}
        {screen && (
          <button
            onClick={onToggleEnabled}
            disabled={busy}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              borderRadius: radii.button,
              border: `1px solid ${enabled ? colors.danger : colors.accent}`,
              background: '#fff',
              color: enabled ? colors.danger : colors.accent,
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer'
            }}
          >
            {enabled ? 'Disable screen' : 'Enable screen'}
          </button>
        )}
      </div>
      <div style={styles.sub}>{screen?.clientId ? screen.clientId : `id ${id}`}</div>

      <div style={styles.statRow}>
        {stat('Last seen', relativeTime(screen?.lastSeenAt))}
        {stat(
          'Now playing',
          screen?.currentAdName ? (
            <>
              {screen.currentAdName}
              {screen.adStartedAt && (
                <span style={{ fontSize: 13, color: colors.subtle, fontWeight: 500 }}>
                  {' '}
                  · {playingFor(screen.adStartedAt)}
                </span>
              )}
            </>
          ) : (
            '—'
          )
        )}
        {stat('Paired driver', screen?.pairedDriverName || '—')}
        {stat('Speed', formatSpeed(screen?.speedKmh))}
        {stat('Location', <span style={{ fontFamily: 'monospace', fontSize: 14 }}>{formatCoord(screen?.latitude, screen?.longitude)}</span>)}
        {stat('GPS points', loading ? '…' : path.length)}
      </div>

      <div style={styles.card}>
        <div style={styles.sectionHead}>
          <div style={styles.sectionTitle}>GPS path replay</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {WINDOWS.map(w => (
              <button key={w.hours} style={windowBtnStyle(windowHours === w.hours)} onClick={() => setWindowHours(w.hours)}>
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {!apiKey ? (
          <div style={{ padding: 36, textAlign: 'center', color: colors.subtle }}>
            Set GOOGLE_MAPS_API_KEY to view the path on a map. {path.length} points loaded for this window.
          </div>
        ) : !isLoaded ? (
          <div style={{ padding: 36, textAlign: 'center', color: colors.subtle }}>Loading map…</div>
        ) : path.length === 0 ? (
          <div style={{ padding: 36, textAlign: 'center', color: colors.subtle }}>
            {loading ? 'Loading path…' : 'No GPS breadcrumbs in this time window.'}
          </div>
        ) : (
          <>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '460px' }}
                center={center}
                zoom={14}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                onClick={() => setSelected(null)}
              >
                {/* Background trail — one polyline per gap-free run, with point dots. */}
                {runs.map(r => (
                  <Polyline
                    key={`bg-${r.start}`}
                    path={latLngs.slice(r.start, r.end + 1)}
                    onClick={selectNearest}
                    options={{
                      strokeColor: '#c7d2fe',
                      strokeWeight: 4,
                      clickable: true,
                      icons: [
                        {
                          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 2, fillColor: '#818cf8', fillOpacity: 1, strokeWeight: 0 },
                          repeat: '14px'
                        }
                      ]
                    }}
                  />
                ))}
                {/* Played portion (accent), clipped to the cursor, per run. */}
                {runs.map(r => {
                  if (r.start > cursor) return null;
                  const end = Math.min(r.end, cursor);
                  if (end <= r.start) return null;
                  return (
                    <Polyline
                      key={`fg-${r.start}`}
                      path={latLngs.slice(r.start, end + 1)}
                      onClick={selectNearest}
                      options={{ strokeColor: colors.accent, strokeWeight: 5, clickable: true }}
                    />
                  );
                })}
                {/* Gap bridges — dashed warning line where GPS was silent > threshold. */}
                {gapStarts.map(i => (
                  <Polyline
                    key={`gap-${i}`}
                    path={[latLngs[i], latLngs[i + 1]]}
                    onClick={selectNearest}
                    options={{
                      strokeColor: colors.danger,
                      strokeOpacity: 0,
                      strokeWeight: 3,
                      clickable: true,
                      zIndex: 3,
                      icons: [
                        {
                          icon: { path: 'M 0,-1 0,1', strokeColor: colors.danger, strokeOpacity: 1, scale: 4 },
                          offset: '0',
                          repeat: '12px'
                        }
                      ]
                    }}
                  />
                ))}
                {path.length > 0 && (
                  <Marker
                    position={latLngs[0]}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: '#10b981', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }}
                    title="Start"
                  />
                )}
                {current && (
                  <Marker
                    position={{ lat: current.latitude, lng: current.longitude }}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: colors.accent, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }}
                    title={formatDateTime(current.recordedAt)}
                  />
                )}
                {selectedPoint && (
                  <Marker
                    position={{ lat: selectedPoint.latitude, lng: selectedPoint.longitude }}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: '#f59e0b', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3 }}
                    zIndex={999}
                  />
                )}
                {selectedPoint && selected != null && (
                  <InfoWindow
                    position={{ lat: selectedPoint.latitude, lng: selectedPoint.longitude }}
                    onCloseClick={() => setSelected(null)}
                    options={{ pixelOffset: new google.maps.Size(0, -10) }}
                  >
                    <div style={styles.info}>
                      <div style={styles.infoHead}>
                        <span style={styles.infoTitle}>Breadcrumb {selected + 1} of {path.length}</span>
                        <span style={styles.infoTime}>{relativeTime(selectedPoint.recordedAt)}</span>
                      </div>
                      {infoRow('Time', formatDateTime(selectedPoint.recordedAt))}
                      {infoRow(
                        'Coordinates',
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'monospace' }}>{formatCoord(selectedPoint.latitude, selectedPoint.longitude)}</span>
                          <button
                            style={styles.infoLink}
                            onClick={() => {
                              navigator.clipboard?.writeText(`${selectedPoint.latitude}, ${selectedPoint.longitude}`);
                              toast('Coordinates copied');
                            }}
                          >
                            Copy
                          </button>
                          <a
                            style={styles.infoLink}
                            href={`https://www.google.com/maps?q=${selectedPoint.latitude},${selectedPoint.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open ↗
                          </a>
                        </span>
                      )}
                      {infoRow('Speed', formatSpeed(selectedPoint.speedKmh))}
                      {infoRow('Heading', formatCourse(selectedPoint.courseDegrees))}
                      {infoRow('GPS fix', formatFixQuality(selectedPoint.fixQuality))}
                      {infoRow('Ad id', selectedPoint.currentAdId != null ? `#${selectedPoint.currentAdId}` : '—')}
                      {infoRow('Campaign run', selectedPoint.campaignRunId != null ? `#${selectedPoint.campaignRunId}` : '—')}
                      {infoRow(
                        'Since previous',
                        prevPoint
                          ? `${formatGap(prevPoint.recordedAt, selectedPoint.recordedAt)} · ${formatDistance(
                              haversineMeters(
                                { lat: prevPoint.latitude, lng: prevPoint.longitude },
                                { lat: selectedPoint.latitude, lng: selectedPoint.longitude }
                              )
                            )}`
                          : '—'
                      )}
                      <button
                        style={styles.infoJump}
                        onClick={() => {
                          setPlaying(false);
                          setCursor(selected);
                        }}
                      >
                        Jump replay here
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14 }}>
              <button style={styles.playBtn} onClick={onPlayToggle} disabled={path.length < 2}>
                {playing ? <PauseIcon /> : <PlayIcon />}
                {playing ? 'Pause' : 'Play'}
              </button>
              <input
                type="range"
                min={0}
                max={Math.max(0, path.length - 1)}
                value={Math.min(cursor, path.length - 1)}
                onChange={e => {
                  setPlaying(false);
                  setCursor(Number(e.target.value));
                }}
                style={{ flex: 1 }}
              />
              <div style={{ minWidth: 190, textAlign: 'right', fontSize: 13, color: colors.muted }}>
                {current ? formatDateTime(current.recordedAt) : '—'}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.muted }}>
                Highlight gaps over
                <input
                  type="number"
                  min={1}
                  value={gapMinutes}
                  onChange={e => {
                    const n = parseInt(e.target.value, 10);
                    setGapMinutes(Number.isNaN(n) ? 1 : Math.max(1, n));
                  }}
                  style={{
                    width: 56,
                    padding: '4px 8px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: radii.input,
                    fontSize: 13,
                    color: colors.title
                  }}
                />
                min
              </label>
              {gapStarts.length > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.subtle }}>
                  <span style={{ display: 'inline-block', width: 26, borderTop: `2px dashed ${colors.danger}` }} />
                  {gapStarts.length} GPS {gapStarts.length === 1 ? 'gap' : 'gaps'} over {gapMinutes} min
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
